/* eslint-disable react-hooks/refs -- intentional render-time ref read +
 * write: this hook is opt-in diagnostic instrumentation gated by
 * RENDER_DEBUG. Refs track previous data identity / status so we can
 * report whether a fire actually changed the data the consumer sees,
 * or only the status object. */
import { useRef } from "react";
import { RENDER_DEBUG } from "@/lib/debug-flags";

/**
 * Diagnostic log for a `useQuery` result tuple.
 *
 * Why a logger and not a wrapper hook:
 *
 * `useQuery` from `@rocicorp/zero/react` has six type parameters and
 * two overloads (Query | Falsy). Wrapping it via spread args collapses
 * the overload to the Maybe-query branch and drops `TReturn` inference
 * at the callsite — every consumer's `data` becomes `unknown`. So we
 * leave the `useQuery` call inline (TypeScript picks the right
 * overload) and immediately hand the returned tuple to this logger:
 *
 *   const result = useQuery(channelId ? queries.channels.byIdComposite(channelId) : null);
 *   useQueryDebugLog("channels.byIdComposite", result);
 *   const [composite] = result;
 *
 * Why this exists:
 *
 * Raw `useQuery` returns a fresh `status` object on every IVM emission
 * even when neither the data nor the result-type changed. That fires
 * a React update in the consuming component on every emission — and
 * there is no built-in way to attribute "MessageArea render #6" back
 * to which underlying Zero subscription emitted the tick.
 *
 * `useZeroSelector` already logs `[useZeroSelector:<key>] CHANGED |
 * STABLE | STATUS-ONLY` on every emission, so subscribers backed by
 * selectors are covered. The blind spot is raw `useQuery` callsites —
 * historically the place new features add subscriptions. This logger
 * closes the gap.
 *
 * Logged on every fire when `RENDER_DEBUG` is on:
 *   [zq:<name>] FIRED type=<unknown|complete|error> rows=<n>
 *               dataIdChanged=<true|false> statusTypeChanged=<true|false>
 *
 * `dataIdChanged=false` + `statusTypeChanged=true` is the "fresh
 * status object only" emission — useful for spotting status-flip
 * render storms.
 *
 * Inert when `RENDER_DEBUG` is off — two ref reads/writes per render
 * (negligible).
 */
export function useQueryDebugLog(
  name: string,
  result: readonly [unknown, { type: string } | undefined],
): void {
  const prevDataRef = useRef<unknown>(null);
  const prevStatusTypeRef = useRef<string | null>(null);

  if (!RENDER_DEBUG) return;

  const [data, status] = result;
  const statusType = status?.type ?? "unknown";
  const dataIdChanged = prevDataRef.current !== data;
  const statusTypeChanged = prevStatusTypeRef.current !== statusType;
  prevDataRef.current = data;
  prevStatusTypeRef.current = statusType;
  if (!dataIdChanged && !statusTypeChanged) return;

  const rows = Array.isArray(data)
    ? data.length
    : data === null || data === undefined
      ? 0
      : 1;
  console.debug(
    `[zq:${name}] FIRED type=${statusType} rows=${rows}`,
    `dataIdChanged=${dataIdChanged} statusTypeChanged=${statusTypeChanged}`,
  );
}
